import React, { useState, useEffect, useCallback } from "react";
import { CyberEssentialsPackage, CyberEssentialsPricing } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
// import { logger } from "@/lib/logger";

interface CyberEssentialsPackageProps {
  blok?: CyberEssentialsPackage & SbBlokData & {
    pricing_cards?: (CyberEssentialsPricing & SbBlokData)[];
  };
}

const PricingCard: React.FC<{
  card: CyberEssentialsPricing & SbBlokData;
  billingPeriod: string;
  employees: string;
  handleCtaClick: (enquireAbout: string) => void;
}> = ({ card, billingPeriod, employees, handleCtaClick }) => {
  // Helper function to determine price display
  const getPriceDisplay = () => {
    const priceKey = billingPeriod === "monthly"
      ? `ce_only_price${employees}`
      : `ce_cyber_tools_price${employees}`;

    const price = card[priceKey as keyof CyberEssentialsPricing] as string | undefined;

    // Check if it's POA case
    if (employees === "50plus" || price === "-1") {
      return {
        isPOA: true,
        displayValue: "POA" as string,
        dataValue: "POA" as string
      };
    }

    return {
      isPOA: false,
      displayValue: price || "",
      dataValue: price || ""
    };
  };

  const priceDisplay = getPriceDisplay();

  // Rest of the helper functions remain unchanged
  const getRemoteSupportText = () => {
    const hours = card.remote_support || "0";
    return `${hours} ${parseInt(hours) === 1 ? "hr" : "hrs"} included`;
  };

  const getFreeRetestText = () => {
    const retests = card.free_retest || "0";
    return `${retests} free retests`;
  };

  return (
    <div className="col js-price-calculator-filter" data-filter="all ce_certifications_only">
      <article className={`card-cyber-essentials ${card.is_most_popular ? "card-most-popular" : ""} js-price-calculator-plan-box h-100`}>
        {card.is_most_popular && <div className="plan-most-popular"><span>Most popular</span></div>}
        <div className="card-cyber-essentials-header d-flex flex-column">
          <h3 className="card-title text-white mb-1">{card.title}</h3>
        </div>
        <div className="lh-1 plan-box-price bg-light text-center mb-0">
          <div className="h1 mb-1">
            {!priceDisplay.isPOA && <span className="card-cyber-essentials-price">£</span>}
            <span className="card-cyber-essentials-price js-card-price"
                  data-monthly-price9={card.ce_only_price9}
                  data-monthly-price20={card.ce_only_price20}
                  data-monthly-price49={card.ce_only_price49}
                  data-monthly-price50plus={card.ce_price50plus === "-1" ? "POA" : card.ce_price50plus}
                  data-yearly-price9={card.ce_cyber_tools_price9}
                  data-yearly-price20={card.ce_cyber_tools_price20}
                  data-yearly-price49={card.ce_cyber_tools_price49}
                  data-yearly-price50plus={card.ce_cyber_tools_price50plus === "-1" ? "POA" : card.ce_cyber_tools_price50plus}
            >
              {priceDisplay.displayValue}
            </span>
          </div>
            <div>
              <span className="small text-muted ms-1">(ex VAT)</span>
            </div>
        </div>
        <div className="card-body card-cyber-essentials-body">
          <ul className="list-unstyled plan-box-features-list text-center mb-0">
            {/* Cyber Tools Feature */}
            <li aria-label="Included"
                className="plan-feature-row d-flex d-xl-flex plan-highlighted-row-content justify-content-between justify-content-lg-center align-items-center mb-2 mt-1">
              <span className="d-xl-none text-truncate text-start me-2">Free additional cyber protection tools</span>
              <i className={`fas ${billingPeriod === "monthly" ? "fa-times text-muted" : "fa-check text-success"}`}
                 aria-hidden="true"></i>
            </li>

            {/* Cyber Essentials Certification */}
            <li aria-label={card.cyber_essentials_certification ? "Included" : "Not included"}
                className="plan-feature-row d-flex d-xl-block justify-content-between align-items-center mb-2 mt-0">
              <span className="d-xl-none text-truncate text-start me-2">Cyber Essentials certification</span>
              <i
                className={`fas ${card.cyber_essentials_certification ? "fa-check text-success" : "fa-times text-danger"} plan-box-icon`}
                aria-hidden="true"></i>
            </li>

            {/* Free Cyber Insurance */}
            <li aria-label={card.up_to_25k_free ? "Included" : "Not included"}
                className="plan-feature-row d-flex d-xl-block justify-content-between align-items-center mb-2 mt-1">
              <span className="d-xl-none text-truncate text-start me-2">Up to 25k FREE cyber insurance<sup>1</sup></span>
              <i className={`fas ${card.up_to_25k_free ? "fa-check text-success" : "fa-times text-danger"} plan-box-icon`}
                 aria-hidden="true"></i>
            </li>

            {/* Cyber Essentials Plus Certification */}
            <li aria-label={card.cyber_essentials_plus_certification ? "Included" : "Not included"}
                className="plan-feature-row d-flex d-xl-block justify-content-between align-items-center mb-2 mt-1">
              <span className="d-xl-none text-truncate text-start me-2">Cyber Essentials Plus certification</span>
              <i
                className={`fas ${card.cyber_essentials_plus_certification ? "fa-check text-success" : "fa-times text-danger"} plan-box-icon`}
                aria-hidden="true"></i>
            </li>

            {/* Tailored Policy Documents */}
            <li aria-label={card.tailored_policy_documents ? "Included" : "Not included"}
                className="plan-feature-row d-flex d-xl-block justify-content-between align-items-center mb-2 mt-1">
              <span className="d-xl-none text-truncate text-start me-2">Tailored policy documents</span>
              <i
                className={`fas ${card.tailored_policy_documents ? "fa-check text-success" : "fa-times text-danger"} plan-box-icon`}
                aria-hidden="true"></i>
            </li>

            {/* Remote Support */}
            <li aria-label="Included"
                className="plan-feature-row d-flex d-xl-block justify-content-between align-items-center mb-2 mt-1">
              <span className="d-xl-none text-truncate text-start me-2">Remote support<sup>3</sup></span>
              <span className="text-nowrap">
                {getRemoteSupportText()}
              </span>
            </li>

            {/* Free Retest */}
            <li aria-label="Included"
                className="plan-feature-row d-flex d-xl-block justify-content-between align-items-center mb-0">
              <span className="d-xl-none text-truncate text-start me-2">Free retest</span>
              <span className="text-nowrap">
                {getFreeRetestText()}
              </span>
            </li>
          </ul>
        </div>
        <div className="card-cyber-essentials-footer">
          <button
            onClick={() => handleCtaClick(`${card.title} - ${employees}`)}
            className="btn btn-secondary w-100 js-cta-btn"
          >
            Enquire
          </button>
        </div>
      </article>
    </div>
  );
};


const CyberEssentialsPackageComponent: React.FC<CyberEssentialsPackageProps> = ({ blok }) => {
  // State declarations
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [employees, setEmployees] = useState("9");
  const [mounted, setMounted] = useState(false);
  const [certOnly] = useState(false);
  const [packagesOnly] = useState(false);

  // UI Update Functions
  const updatePlanFeatureRows = useCallback(() => {
    const shouldShowAsNotIncluded = certOnly || packagesOnly;
    const planFeatureRows = document.querySelectorAll(".plan-feature-row");
    const planFeatureRowIcons = document.querySelectorAll(".plan-highlighted-row-content .plan-box-icon");

    planFeatureRows.forEach(row => {
      row.classList.toggle("not-included", shouldShowAsNotIncluded);
    });

    planFeatureRowIcons.forEach(icon => {
      if (shouldShowAsNotIncluded) {
        icon.classList.remove("fa-check", "text-success");
        icon.classList.add("not-included", "fa-times");
      } else {
        icon.classList.remove("not-included", "fa-times");
        icon.classList.add("fa-check", "text-success");
      }
    });
  }, [certOnly, packagesOnly]);

  const updatePriceValues = useCallback(() => {
    const priceElements = document.querySelectorAll(".js-card-price");
    const periodElements = document.querySelectorAll(".js-card-price-period");

    priceElements.forEach(element => {
      const priceEl = element as HTMLElement;
      const newPrice = priceEl.dataset[`${billingPeriod}Price${employees}`];
      if (newPrice) {
        if (newPrice === "POA") {
          priceEl.innerText = newPrice;
        } else {
          const price = Number(newPrice);
          const hasDecimals = price % 1 !== 0;
          priceEl.innerText = hasDecimals
            ? price.toFixed(2)
            : price.toString();
        }
      }
    });

    periodElements.forEach(element => {
      const periodEl = element as HTMLElement;
      const newText = periodEl.dataset[`${billingPeriod}Text`];
      if (newText) {
        periodEl.innerText = newText;
      }
    });
  }, [billingPeriod, employees]);

  const updateProductCodes = useCallback(() => {
    const toolButtons = document.querySelectorAll(".btn-tools");
    toolButtons.forEach(button => {
      const btn = button as HTMLElement;
      if (certOnly) {
        btn.setAttribute("value", btn.dataset.woTools || "");
      } else {
        btn.setAttribute("value", `${btn.dataset.withTools}${employees}`);
      }
    });
  }, [certOnly, employees]);

  // Main Filter Update Function
  const updateFilters = useCallback(() => {
    const newFilters: string[] = [];

    if (certOnly || packagesOnly) {
      if (certOnly) {
        newFilters.push("ce_certifications_only");
      }
      if (packagesOnly) {
        newFilters.push("ce_packages_only");
      }
    } else {
      newFilters.push("all");
    }

    const filterElements = document.querySelectorAll(".js-price-calculator-filter");
    filterElements.forEach((element) => {
      const filterEl = element as HTMLElement;
      if (filterEl.dataset.filter) {
        let found = false;
        newFilters.forEach((filter) => {
          if (filterEl.dataset.filter && filterEl.dataset.filter.includes(filter)) {
            found = true;
          }
        });
        filterEl.classList.toggle("filter-hide", !found);
      }
    });

    if (certOnly && employees !== "50plus") {
      setEmployees("9");
    }

    updatePlanFeatureRows();
    updatePriceValues();
    updateProductCodes();
  }, [certOnly, packagesOnly, employees, updatePlanFeatureRows, updatePriceValues, updateProductCodes]);


  // Event Handlers
  const handleBillingPeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBillingPeriod(e.target.value);
  };

  const handleEmployeeCountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEmployees(e.target.value);
  };

  const handleCtaClick = (enquireAbout: string) => {
    const quoteService = document.getElementById("quote_service") as HTMLInputElement;
    if (quoteService) {
      quoteService.value = enquireAbout;
    }

    const getQuoteElement = document.getElementById("getQuote");
    if (getQuoteElement) {
      getQuoteElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  // const handleBuyNow = (product: string) => {
  //   const queryParams = new URLSearchParams({
  //     product,
  //     billing_period: billingPeriod,
  //     filter_type: certOnly ? "ce_certifications_only" :
  //       packagesOnly ? "ce_packages_only" : "all",
  //     employees,
  //   });
  //   window.location.href = `/purchases?${queryParams.toString()}`;
  // };

  // Effects
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      updateFilters();
    }
  }, [mounted, updateFilters]);

  if (!blok) return null;

  const { title, subtitle, alert } = blok;


  return (
    <section {...storyblokEditable(blok)} className="bg-light py-4 py-md-5">
      <div className="container">
        <h2 className="text-center">{title}</h2>
        <p className="text-center lead mb-3 mb-xl-4">{subtitle}</p>

        {alert && (
          <div className="alert alert-info mb-3 mb-xl-4 float-none w-100 text-center">
            {alert}
          </div>
        )}

        {/* Pricing Grid Section */}
        <div className="row gy-4 mb-5">
          <div className="col-xl-6">
            <div className="adjuster">
              <div className="card card-lighter p-3 flex-md-row flex-xl-column flex-wrap">
                {/* How will you pay */}
                <div className="pe-md-2 p-xl-0 col-24 col-md-12 col-lg-6 col-xl-24 mb-2 mb-lg-0 order-1">
                  <div className="row gy-3 gy-md-0 gx-lg-4 gy-xl-3 mb-xl-3">
                    <div className="col-24">
                      <div className="btn-toggle-wrapper js-billing-period-wrapper">
                        <label className="btn-toggle-label mb-1">Select option</label>
                        <div className="btn-toggle">
                          <input
                            type="radio"
                            name="billing_period"
                            id="billing_period_monthly"
                            value="monthly"
                            checked={billingPeriod === "monthly"}
                            onChange={handleBillingPeriodChange}
                            className="btn-toggle-input js-billing-toggle"
                          />
                          <label htmlFor="billing_period_monthly"
                                 className="flex-grow-1 w-xl-50 btn-toggle-btn mb-0 d-flex align-items-center text-wrap">
                            {blok.how_will_you_pay_label_1}
                          </label>
                          <input
                            type="radio"
                            name="billing_period"
                            id="billing_period_yearly"
                            value="yearly"
                            checked={billingPeriod === "yearly"}
                            onChange={handleBillingPeriodChange}
                            className="btn-toggle-input js-billing-toggle"
                          />
                          <label htmlFor="billing_period_yearly"
                                 className="flex-grow-1 w-xl-50 btn-toggle-btn mb-0 text-wrap">
                            {blok.how_will_you_pay_label_2}
                            <span className="small d-block fw-normal mt-1">{blok.how_will_you_pay_label_2_free}</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Toggles */}
                {/*<div*/}
                {/*  className="pe-md-2 ps-lg-2 p-xl-0 col-24 col-md-12 col-lg-6 col-xl-24 mb-md-0 mb-xl-0 order-2 text-center">*/}
                {/*  <div className="row gy-3 gy-md-0 gx-lg-4 gy-xl-3 mb-xl-1">*/}
                {/*    <div className="col-auto">*/}
                {/*      <div className="form-check form-switch">*/}
                {/*        <input*/}
                {/*          className="form-check-input js-package-filter"*/}
                {/*          name="filter_type"*/}
                {/*          type="checkbox"*/}
                {/*          role="switch"*/}
                {/*          id="ce_certifications_only"*/}
                {/*          checked={certOnly}*/}
                {/*          onChange={handleFilterChange}*/}
                {/*          value="ce_certifications_only"*/}
                {/*        />*/}
                {/*        <label className="form-check-label" htmlFor="ce_certifications_only">*/}
                {/*          View certifications only*/}
                {/*        </label>*/}
                {/*      </div>*/}
                {/*    </div>*/}
                {/*  </div>*/}
                {/*</div>*/}

                {/* Toggle 2 */}
                {/*<div className="ps-md-2 p-xl-0 col-24 col-md-12 col-lg-6 col-xl-24 mb-2 mb-md-0 order-3">*/}
                {/*  <div className="row gy-3 gy-md-0 gx-lg-4 gy-xl-3 mb-xl-1">*/}
                {/*    <div className="col-auto">*/}
                {/*      <div className="form-check form-switch">*/}
                {/*        <input*/}
                {/*          className="form-check-input js-package-filter"*/}
                {/*          name="filter_type"*/}
                {/*          type="checkbox"*/}
                {/*          role="switch"*/}
                {/*          id="ce_packages_only"*/}
                {/*          checked={packagesOnly}*/}
                {/*          onChange={handleFilterChange}*/}
                {/*          value="ce_packages_only"*/}
                {/*        />*/}
                {/*        <label className="form-check-label" htmlFor="ce_packages_only">*/}
                {/*          View upgrades only*/}
                {/*        </label>*/}
                {/*      </div>*/}
                {/*    </div>*/}
                {/*  </div>*/}
                {/*</div>*/}

                {/* Number of employees */}
                <div
                  className="ps-md-2 p-xl-0 col-24 col-md-12 col-lg-6 col-xl-24 mb-md-2 mb-lg-0 order-4 order-md-1 order-xl-4">
                  <div className="pricing-controls-col pricing-controls-select mb-0 pricing-control-employees">
                    <label htmlFor="employees">Number of employees</label>
                    <div className="styled-select darker">
                      <select
                        name="employees"
                        id="employees"
                        className="js-employee-count-toggle"
                        value={employees}
                        onChange={handleEmployeeCountChange}
                      >
                        <option value="9">1-9</option>
                        <option value="20">10-20</option>
                        <option value="49">21-49</option>
                        <option value="50plus">50+</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Features List */}
            <div className="d-none d-xl-block">
              <section>
                <h3 className="text-secondary fw-bolder mb-3">Includes</h3>
                <ul className="plan-box-features-list list-unstyled text-dark mb-0">
                  <li
                    className={`plan-feature-row mb-2 plan-highlighted-row ${billingPeriod === "monthly" ? "certification-only" : ""}`}>
                    <strong className="d-block text-secondary mb-2">
                      Cyber tools <span className="d-none d-xxl-inline">required</span> to pass:
                    </strong>
                    <ul className="sub list-checked mb-0">
                      <li>Anti-virus protection</li>
                      <li>Training</li>
                      <li>Vulnerability scanning</li>
                      <li>Phishing simulator</li>
                      <li>Threat dashboard</li>
                      <li>Asset tracker</li>
                      <li><strong>+ more</strong></li>
                    </ul>
                  </li>
                  <li className="plan-feature-row mb-2">Cyber Essentials certification</li>
                  <li className="plan-feature-row mb-2">Up to 25k FREE cyber insurance</li>
                  <li className="plan-feature-row mb-2">Cyber Essentials Plus certification</li>
                  <li className="plan-feature-row mb-2">Tailored policy documents</li>
                  <li className="plan-feature-row mb-2">Remote support</li>
                  <li className="plan-feature-row mb-0">Free retest</li>
                </ul>
              </section>
            </div>
          </div>

          {/* Pricing Cards Column */}
          <div className="col-lg">
            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 gy-3 gx-md-3">
              {blok?.pricing_cards?.map((card) => (
                <PricingCard
                  key={card._uid}
                  card={card as CyberEssentialsPricing & SbBlokData}
                  billingPeriod={billingPeriod}
                  employees={employees}
                  handleCtaClick={handleCtaClick}
                />
              ))}
            </div>
          </div>

        </div>


        {/*<div className="row text-center justify-content-center align-items-center mb-4">*/}
        {/*  <div className="col-24 col-md-auto">*/}
        {/*    <Image*/}
        {/*      src={visaImg}*/}
        {/*      width={64}*/}
        {/*      height={43}*/}
        {/*      className="img-fluid me-1 border rounded-3 d-inline-block"*/}
        {/*      alt="Visa"*/}
        {/*    />*/}
        {/*    <Image*/}
        {/*      src={mastercardImg}*/}
        {/*      width={64}*/}
        {/*      height={43}*/}
        {/*      className="img-fluid me-1 border rounded-3 d-inline-block"*/}
        {/*      alt="Mastercard"*/}
        {/*    />*/}
        {/*    <Image*/}
        {/*      src={maestroImg}*/}
        {/*      width={64}*/}
        {/*      height={43}*/}
        {/*      className="img-fluid me-1 border rounded-3 d-inline-block"*/}
        {/*      alt="Maestro"*/}
        {/*    />*/}
        {/*    <Image*/}
        {/*      src={amexImg}*/}
        {/*      width={64}*/}
        {/*      height={43}*/}
        {/*      className="img-fluid me-1 border rounded-3 d-inline-block"*/}
        {/*      alt="America Express"*/}
        {/*    />*/}
        {/*    <Image*/}
        {/*      src={discoverImg}*/}
        {/*      width={64}*/}
        {/*      height={43}*/}
        {/*      className="img-fluid me-1 border rounded-3 d-inline-block"*/}
        {/*      alt="Discover"*/}
        {/*    />*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/*<div className="row text-center justify-content-center align-items-center">*/}
        {/*  <div className="col-24 col-md-auto lead mb-4 mb-md-0">*/}
        {/*    <i className="fas fa-lock fa-fw fa-lg me-2"></i>*/}
        {/*    Guaranteed <strong>safe &amp; secure checkout</strong>*/}
        {/*  </div>*/}
        {/*  <div className="col-24 col-md-auto">*/}
        {/*    <Image*/}
        {/*      src={stripeImg}*/}
        {/*      width={160}*/}
        {/*      height={44}*/}
        {/*      alt="Powered by Stripe"*/}
        {/*    />*/}
        {/*  </div>*/}
        {/*</div>*/}
      </div>
    </section>
  );
};

export default CyberEssentialsPackageComponent;